import axios from 'axios';
import { environment } from '../../environments/environment';

/**
 * Run counts for flows and agent nodes, read from the orchestrator's execution
 * logs by the control plane.
 *
 * Follows caseWorkflowService: one private `get`, the `{ status, data }`
 * envelope unwrapped in one place, and `null` for any count the platform can't
 * measure yet rather than a plausible 0.
 */

const getBaseUrl = () => `${environment.controlplaneUrl}/api/v1/execution-stats`;

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

/* ------------------------------------------------------------------- types */

export type Scope = 'flow' | 'node';
/** What counts are grouped by: a template code, or a campaign (flow runs only). */
export type KeyType = 'code' | 'campaign';
export type WindowKey = '24h' | '7d' | '30d';
export type Bucket = 'success' | 'failed' | 'pendingReview' | 'running' | 'stalled';
export type Granularity = 'day' | 'week' | 'month';
export type BreakdownSort = 'total' | 'failed' | 'failureRate' | 'pendingReview';

/**
 * Every run lands in exactly one bucket. A bucket the platform can't measure
 * yet is null — "not tracked", which must never render as 0.
 */
export interface BucketCounts {
  total: number;
  success: number;
  failed: number;
  pendingReview: number | null;
  running: number | null;
  stalled: number | null;
}

interface Envelope {
  scope: Scope;
  keyType: KeyType;
  generatedAt: string;
  /** An In Progress run older than this is counted as stalled. */
  staleAfterHours: number;
  /**
   * When the orchestrator started recording pending reviews and real node
   * status; null until it does. A window that starts before it has no
   * pending-review count.
   */
  runtimeStatusSince: string | null;
  /** Holds for every count in the response; a window or period past the cutover may track more. */
  tracked: { pendingReview: boolean; running: boolean };
}

export interface SummaryResponse extends Envelope {
  windows: WindowKey[];
  stats: Record<string, Partial<Record<WindowKey, BucketCounts>>>;
  /**
   * Campaigns only. `lastRunAt` is the campaign's latest run of its own flow
   * over all time; null means its runs have never been tagged with it.
   */
  meta?: Record<string, { flowCode: string | null; lastRunAt: string | null }>;
}

export interface TimeseriesPoint extends BucketCounts {
  /** The period's local start date in `timezone`, YYYY-MM-DD. */
  period: string;
}

export interface TimeseriesResponse extends Envelope {
  granularity: Granularity;
  timezone: string;
  from: string;
  to: string;
  totals: BucketCounts;
  points: TimeseriesPoint[];
}

export interface BreakdownRow extends BucketCounts {
  key: string;
  name: string | null;
  /** Failed over finished (success + failed) runs; null with none finished. */
  failureRate: number | null;
  lastRunAt: string | null;
}

export interface BreakdownResponse extends Envelope {
  from: string;
  to: string;
  sort: BreakdownSort;
  rows: BreakdownRow[];
}

export interface RunRow {
  referenceId: string | null;
  /** The run's own name, falling back to the workflow's. */
  name: string | null;
  /** The template code, version suffix included. */
  code?: string | null;
  campaignId?: string | null;
  workflowName?: string | null;
  instanceName?: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  /** null when the run's bucket isn't one the platform tracks for this scope. */
  bucket: Bucket | null;
  status: string | null;
  errorMessage: string | null;
}

export interface RunsResponse extends Envelope {
  /** null for the org-wide run log. */
  key: string | null;
  bucket: Bucket | null;
  runs: RunRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

/** One agent node the run executed, from the activity log. */
export interface RunStep {
  code: string | null;
  name: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  bucket: Bucket | null;
  status: string | null;
  errorMessage: string | null;
}

export interface RunDetail {
  generatedAt: string;
  staleAfterHours: number;
  runtimeStatusSince: string | null;
  tracked: {
    flow: { pendingReview: boolean; running: boolean };
    node: { pendingReview: boolean; running: boolean };
  };
  run: RunRow & {
    context: { industry: string | null; language: string | null; geography: string | null };
  };
  /** The flow as configured: node names and order only, never parameters. */
  configuration: {
    nodes: { id: string | null; code: string | null; name: string | null }[];
    edges: { source: string | null; target: string | null }[];
  };
  /** null when this environment doesn't serve node steps. */
  steps: RunStep[] | null;
}

export interface RunsQuery extends RangeQuery {
  bucket?: Bucket | null;
  /** An exact reference id, or part of a run or workflow name. */
  q?: string | null;
  /** Unkeyed lists leave BENCHMARK_ replays out unless this is set. */
  includeBenchmarks?: boolean;
  page?: number;
  limit?: number;
}

/** Which runs to count: template codes or campaign ids, never both. */
export interface KeySelection {
  scope?: Scope;
  keyType?: KeyType;
  keys?: string[];
  groupVersions?: boolean;
}

export interface RangeQuery extends KeySelection {
  from: string;
  to: string;
}

interface RequestOptions {
  signal?: AbortSignal;
}

/* ------------------------------------------------------------------ errors */

export class ExecutionStatsRequestError extends Error {
  readonly status: number | null;
  readonly violations: { field: string; message: string }[];

  constructor(message: string, status: number | null, violations: { field: string; message: string }[] = []) {
    super(message);
    this.name = 'ExecutionStatsRequestError';
    this.status = status;
    this.violations = violations;
  }
}

function toRequestError(error: any): ExecutionStatsRequestError {
  const status = error?.response?.status ?? null;
  if (status === 401 || status === 403) {
    return new ExecutionStatsRequestError('Your session has expired. Sign in again to view run statistics.', status);
  }
  if (status === 404) {
    return new ExecutionStatsRequestError(
      'Not found. It may belong to another organisation, or its log may have been removed.',
      status
    );
  }
  if (status === 422) {
    const violations = error?.response?.data?.violations ?? [];
    return new ExecutionStatsRequestError(
      violations[0]?.message || 'That view is not available.',
      status,
      violations
    );
  }
  const serverMessage = error?.response?.data?.error;
  return new ExecutionStatsRequestError(
    serverMessage || 'Could not load run statistics. Check your connection and try again.',
    status
  );
}

export const isAbort = (error: unknown): boolean => axios.isCancel(error);

async function get<T>(path: string, params: Record<string, any>, { signal }: RequestOptions = {}): Promise<T> {
  try {
    const res = await axios.get(`${getBaseUrl()}${path}`, { headers: getHeaders(), params, signal });
    return res.data?.data as T;
  } catch (error) {
    if (isAbort(error)) throw error;
    throw toRequestError(error);
  }
}

/** The key parameters as the API reads them. */
function keyParams({ scope = 'flow', keyType = 'code', keys, groupVersions }: KeySelection, single = false) {
  const params: Record<string, string> = { scope };
  if (keys && keys.length) {
    const name = keyType === 'campaign'
      ? (single ? 'campaignId' : 'campaignIds')
      : (single ? 'code' : 'codes');
    params[name] = keys.join(',');
  }
  if (groupVersions) params.groupVersions = 'true';
  return params;
}

/** The viewer's IANA zone, so periods are the viewer's calendar days. */
export const viewerTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const executionStatsService = {
  getSummary(selection: KeySelection & { windows?: WindowKey[] }, options?: RequestOptions) {
    return get<SummaryResponse>('/summary', {
      ...keyParams(selection),
      ...(selection.windows ? { windows: selection.windows.join(',') } : {})
    }, options);
  },

  getTimeseries(query: RangeQuery & { granularity: Granularity; tz?: string }, options?: RequestOptions) {
    return get<TimeseriesResponse>('/timeseries', {
      ...keyParams(query),
      from: query.from,
      to: query.to,
      granularity: query.granularity,
      tz: query.tz || viewerTimeZone()
    }, options);
  },

  getBreakdown(query: RangeQuery & { sort?: BreakdownSort; limit?: number }, options?: RequestOptions) {
    return get<BreakdownResponse>('/breakdown', {
      ...keyParams(query),
      from: query.from,
      to: query.to,
      ...(query.sort ? { sort: query.sort } : {}),
      ...(query.limit ? { limit: query.limit } : {})
    }, options);
  },

  /** The runs behind a key's counts, or with no key, every run in the org. */
  listRuns(query: RunsQuery, options?: RequestOptions) {
    return get<RunsResponse>('/runs', {
      ...keyParams(query, true),
      from: query.from,
      to: query.to,
      ...(query.bucket ? { bucket: query.bucket } : {}),
      ...(query.q ? { q: query.q } : {}),
      ...(query.includeBenchmarks ? { includeBenchmarks: 'true' } : {}),
      page: query.page ?? 1,
      limit: query.limit ?? 25
    }, options);
  },

  getRun(referenceId: string, options?: RequestOptions) {
    return get<RunDetail>(`/runs/${encodeURIComponent(referenceId)}`, {}, options);
  }
};
